import { AxiosInstance } from 'axios';
import Keycloak, { KeycloakConfig } from 'keycloak-js';
import createAuthRefreshInterceptor from 'refresh';

import type { AxiosAuthRefreshCallback } from 'v2/types';

import type { IAuthConfig, IAuthProvider } from 'v2/providers/types';

import { authStorage } from './storages';
import { createResponseInterceptor, requestInterceptor } from './interceptors';
import { iFrameKeyCloakConfig, standardKeyCloakConfig } from './config';
import type { IKeycloakProviderOptions } from './types';

export class KeyCloakAuthProvider implements IAuthProvider {
  private _config: KeycloakConfig & IAuthConfig;

  private _options: IKeycloakProviderOptions;

  private readonly _kc: Keycloak;

  private readonly _responseInterceptor: AxiosAuthRefreshCallback;

  public isInitialized = false;

  constructor(config: KeycloakConfig & IAuthConfig, options?: IKeycloakProviderOptions) {
    this._config = config;
    this._options = options ?? {};

    this._kc = new Keycloak(config);
    this._responseInterceptor = createResponseInterceptor(
      this._kc,
      !!this._config.allowAnonymous,
    );

    this.login = this.login.bind(this);
    this.logout = this.logout.bind(this);
  }

  async init() {
    const keycloakTokens = authStorage.getJwtAuthParams();

    if (!this.isInitialized) {
      await this._kc.init({
        ...(this._options.useStandardFlow
          ? standardKeyCloakConfig
          : iFrameKeyCloakConfig),
        enableLogging: !!this._options.enableLogging,
        token: keycloakTokens?.token,
        refreshToken: keycloakTokens?.refreshToken,
        idToken: keycloakTokens?.idToken,
      });

      this.isInitialized = true;
    }

    if (await this._tryUpdateToken()) {
      return true;
    }

    if (!this._config.allowAnonymous) {
      await this._kc.login();
    }

    return false;
  }

  private async _storeTokens() {
    if (this._kc.token && this._kc.refreshToken) {
      await authStorage.setJwtAuthParams({
        token: this._kc.token,
        refreshToken: this._kc.refreshToken,
        idToken: this._kc.idToken,
      });
    }
  }

  private async _tryUpdateToken(): Promise<boolean> {
    try {
      await this._kc.updateToken(5);
      await this._storeTokens();

      return true;
    } catch {
      return false;
    }
  }

  login() {
    return this._kc.login();
  }

  async logout(redirectUrl?: string) {
    await authStorage.clearJwtAuthParams();

    return this._kc.logout({ redirectUri: redirectUrl });
  }

  getLogoutUrl(redirectUrl?: string): string {
    return this._kc.createLogoutUrl({ redirectUri: redirectUrl });
  }

  getToken(): string | undefined {
    if (this._kc.token) return this._kc.token;

    return authStorage.getJwtAuthParams()?.token;
  }

  getLoginUrl() {
    return this._kc.createLoginUrl();
  }

  isLoggedIn() {
    return Boolean(this.getToken());
  }

  getRoles() {
    return this._kc.tokenParsed?.resource_access?.account;
  }

  setAuthInterceptor(instance: AxiosInstance) {
    instance.interceptors.request.use(requestInterceptor);
    createAuthRefreshInterceptor(instance, this._responseInterceptor, {
      statusCodes: [401],
    });
  }
}
